import * as React from 'react'

import { sanitizeHtmlValue } from '@/lib/element-schema-sanitizers'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { PageHeader, PageHeaderDescription, PageHeaderHeading } from '@/components/common/page-header'
import { Shell } from '@/components/common/shell'

import type { BackgroundValue, SectionElementSchema } from '@/types/element-schema.type'

/**
 * Props for SectionHeroV3
 * Supports full CMS configuration via Puck editor
 */
interface SectionHeroV3Props {
  /** Main heading text (supports HTML) */
  heading?: React.ReactNode
  /** Description text (supports HTML) */
  description?: React.ReactNode
  /** Required for backward compatibility - not used in CMS mode */
  isCourseEnrolled?: boolean
  /** Additional CSS classes */
  className?: string
  /** @deprecated Use bgGradientDirection instead */
  gradientSide?: 'left' | 'right'
  // === Background Configuration ===
  /** Background type */
  bgType?: 'none' | 'color' | 'gradient' | 'image' | 'video'
  /** Solid background color */
  bgColor?: string
  /** Gradient start color */
  bgGradientFrom?: string
  /** Gradient end color */
  bgGradientTo?: string
  /** Gradient direction */
  bgGradientDirection?: 'to-r' | 'to-l' | 'to-b' | 'to-t'
  /** Gradient opacity (95 = 95%) */
  bgGradientOpacity?: '100' | '95' | '90' | '80' | '70' | '60'
  /** Background image URL */
  bgImage?: string
  /** Background video URL */
  bgVideo?: string
  /** Show overlay on image/video */
  overlay?: boolean
  // === Image Position & Size ===
  /** CSS background-position for the background image */
  bgImagePosition?: string
  /** CSS background-size for the background image */
  bgImageSize?: string
  // === Layout Configuration ===
  /** Text alignment */
  textAlignment?: 'left' | 'center' | 'right'
  /** Full background configuration object (alternative to individual props) */
  background?: BackgroundValue
}

/**
 * Get gradient direction Tailwind classes
 */
function getGradientClasses(direction?: string): string {
  const directionMap: Record<string, string> = {
    'to-r': 'bg-gradient-to-r',
    'to-l': 'bg-gradient-to-l',
    'to-b': 'bg-gradient-to-b',
    'to-t': 'bg-gradient-to-t',
  }
  return directionMap[direction || 'to-r'] || 'bg-gradient-to-r'
}

function SectionHeroV3({
  heading,
  description,
  className,
  gradientSide,
  // Background props
  bgType = 'image',
  bgGradientFrom = '#bf2318',
  bgGradientTo = '#a52118',
  bgGradientDirection,
  bgGradientOpacity = '95',
  bgImage = '/images/home/background-home-page-v2.png',
  bgVideo,
  overlay = true,
  // Image position & size props
  bgImagePosition = 'center center',
  bgImageSize = 'cover',
  // Layout props
  textAlignment = 'center',
  // Full background object (alternative)
  background,
}: SectionHeroV3Props) {
  const { text } = useLocale()

  // Determine text alignment classes
  const textAlignClasses = {
    left: 'text-left items-start justify-start',
    center: 'text-center items-center justify-center',
    right: 'text-right items-end justify-end',
  }

  // Use individual props or background object
  const actualBgType = background?.type || bgType
  const actualGradientFrom = background?.gradient?.colors?.[0] || bgGradientFrom
  const actualGradientTo = background?.gradient?.colors?.[1] || bgGradientTo
  const actualBgImage = background?.image?.src || bgImage
  const actualBgVideo = background?.video?.src || bgVideo

  // Determine gradient direction (support legacy gradientSide prop)
  const actualGradientDirection = bgGradientDirection || (gradientSide === 'right' ? 'to-l' : 'to-r')

  // Render video background
  if (actualBgType === 'video' && actualBgVideo) {
    return (
      <div className={cn('relative z-10 overflow-hidden', className)}>
        <video
          className="absolute inset-0 w-full h-full object-cover"
          src={actualBgVideo}
          autoPlay
          loop
          muted
          playsInline
        />
        {overlay && (
          <div
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background: `linear-gradient(${actualGradientDirection === 'to-l' ? 'to left' : actualGradientDirection === 'to-b' ? 'to bottom' : actualGradientDirection === 'to-t' ? 'to top' : 'to right'}, ${actualGradientFrom}dd, ${actualGradientTo}99)`,
            }}
          />
        )}
        <div className="md:min-h-[calc(100vh-80px)] relative z-10">
          <Shell>
            <PageHeader
              className={cn('space-y-4 sm:space-y-6 md:space-y-8 w-full mx-auto mt-6', textAlignClasses[textAlignment])}
            >
              <PageHeaderHeading
                as="h2"
                size="lg"
                className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight font-extrabold text-white"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlValue((heading as string) || text.MAIN.INTRODUCTION.HEADING_2),
                }}
              />
              <PageHeaderDescription
                className={cn(
                  'text-base sm:text-lg md:text-xl mx-auto text-white !mt-2',
                  textAlignClasses[textAlignment]
                )}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtmlValue((description as string) || text.MAIN.INTRODUCTION.HEADING_3),
                }}
              />
            </PageHeader>
          </Shell>
        </div>
      </div>
    )
  }

  // Render image or gradient background
  const hasBgImage = actualBgType === 'image' && actualBgImage
  const hasGradient = actualBgType === 'gradient' || (hasBgImage && overlay)

  return (
    <div className={cn('relative z-10', className)}>
      {/* Gradient overlay */}
      {hasGradient && (
        <div
          className={cn('pointer-events-none absolute inset-0 z-0', getGradientClasses(actualGradientDirection))}
          style={{
            background: `linear-gradient(${actualGradientDirection === 'to-l' ? 'to left' : actualGradientDirection === 'to-b' ? 'to bottom' : actualGradientDirection === 'to-t' ? 'to top' : 'to right'}, ${actualGradientFrom}${bgGradientOpacity === '100' ? '' : bgGradientOpacity}, ${actualGradientTo}${bgGradientOpacity === '100' ? '' : '60'})`,
          }}
        />
      )}
      {/* Main content with optional background image */}
      <div
        className={cn('md:min-h-[calc(100vh-80px)] overflow-hidden relative z-10', hasBgImage && 'bg-no-repeat')}
        style={
          hasBgImage
            ? {
                backgroundImage: `url(${actualBgImage})`,
                backgroundPosition: bgImagePosition,
                backgroundSize: bgImageSize,
              }
            : undefined
        }
      >
        <Shell>
          <PageHeader
            className={cn('space-y-4 sm:space-y-6 md:space-y-8 w-full mx-auto mt-6', textAlignClasses[textAlignment])}
          >
            <PageHeaderHeading
              as="h2"
              size="lg"
              className="tracking-normal text-3xl sm:text-4xl md:text-5xl lg:text-6xl/tight md:leading-tight font-extrabold text-white"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlValue((heading as string) || text.MAIN.INTRODUCTION.HEADING_2),
              }}
            />
            <PageHeaderDescription
              className={cn(
                'text-base sm:text-lg md:text-xl mx-auto text-white !mt-2',
                textAlignClasses[textAlignment]
              )}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtmlValue((description as string) || text.MAIN.INTRODUCTION.HEADING_3),
              }}
            />
          </PageHeader>
        </Shell>
      </div>
    </div>
  )
}

/**
 * Element schema for SectionHeroV3
 * Defines all editable elements for the hero section
 */
SectionHeroV3.elementSchema = {
  componentType: 'SectionHeroV3',
  label: 'Hero Section',
  description: 'Main hero section with heading, description, and configurable background',
  category: 'hero',
  tags: ['landing', 'home'],
  elements: [
    // Text Content
    {
      key: 'heading',
      type: 'textarea',
      label: 'Heading',
      helpText: 'Main heading text (supports HTML)',
      required: false,
    },
    {
      key: 'description',
      type: 'textarea',
      label: 'Description',
      helpText: 'Subheading or description text (supports HTML)',
      required: false,
    },
    // Layout
    {
      key: 'textAlignment',
      type: 'select',
      label: 'Text Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      default: 'center',
    },
    // Background Configuration
    {
      key: 'bgType',
      type: 'select',
      label: 'Background Type',
      options: [
        { label: 'None', value: 'none' },
        { label: 'Color', value: 'color' },
        { label: 'Gradient', value: 'gradient' },
        { label: 'Image', value: 'image' },
        { label: 'Video', value: 'video' },
      ],
      default: 'image',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      helpText: 'Solid background color (when Type is Color)',
    },
    {
      key: 'bgGradientFrom',
      type: 'color',
      label: 'Gradient Start',
      helpText: 'Starting color for gradient overlay',
      default: '#bf2318',
    },
    {
      key: 'bgGradientTo',
      type: 'color',
      label: 'Gradient End',
      helpText: 'Ending color for gradient overlay',
      default: '#a52118',
    },
    {
      key: 'bgGradientDirection',
      type: 'select',
      label: 'Gradient Direction',
      options: [
        { label: 'Left to Right', value: 'to-r' },
        { label: 'Right to Left', value: 'to-l' },
        { label: 'Top to Bottom', value: 'to-b' },
        { label: 'Bottom to Top', value: 'to-t' },
      ],
      default: 'to-r',
    },
    {
      key: 'bgGradientOpacity',
      type: 'select',
      label: 'Gradient Opacity',
      options: [
        { label: '100%', value: '100' },
        { label: '95%', value: '95' },
        { label: '90%', value: '90' },
        { label: '80%', value: '80' },
        { label: '70%', value: '70' },
        { label: '60%', value: '60' },
      ],
      default: '95',
    },
    {
      key: 'bgImage',
      type: 'image',
      label: 'Background Image',
      helpText: 'URL for background image (when Type is Image)',
      default: '/images/home/background-home-page-v2.png',
    },
    {
      key: 'bgVideo',
      type: 'video',
      label: 'Background Video',
      helpText: 'URL for background video (when Type is Video)',
    },
    {
      key: 'overlay',
      type: 'boolean',
      label: 'Show Overlay',
      helpText: 'Add gradient overlay on top of image/video',
      default: true,
    },
    // Image Position & Size
    {
      key: 'bgImagePosition',
      type: 'select',
      label: 'Image Position',
      helpText: 'CSS background-position for the background image',
      options: [
        { label: 'Center', value: 'center center' },
        { label: 'Top', value: 'center top' },
        { label: 'Bottom', value: 'center bottom' },
        { label: 'Left', value: 'left center' },
        { label: 'Right', value: 'right center' },
        { label: 'Top Left', value: 'left top' },
        { label: 'Top Right', value: 'right top' },
        { label: 'Bottom Left', value: 'left bottom' },
        { label: 'Bottom Right', value: 'right bottom' },
      ],
      default: 'center center',
    },
    {
      key: 'bgImageSize',
      type: 'select',
      label: 'Image Size',
      helpText: 'CSS background-size for the background image',
      options: [
        { label: 'Cover', value: 'cover' },
        { label: 'Contain', value: 'contain' },
        { label: 'Auto', value: 'auto' },
        { label: '100%', value: '100%' },
        { label: '150%', value: '150%' },
        { label: '200%', value: '200%' },
      ],
      default: 'cover',
    },
    // Styling
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes for custom styling',
    },
  ],
} as SectionElementSchema

export default SectionHeroV3
